import React from "react";

/**
 * CoveragePanel — data_coverage evidence component.
 *
 * Small grid of coverage stats near the bottom of the Detail tab.
 * The honesty panel: shows how complete the data is for this property.
 *
 * Block design mirrors Google Analytics "Period at a Glance" IntelligenceCards
 * — coloured top border, icon chip, uppercase title, bold value, optional
 * detail — compacted to a single row of 6 box-shaped cards.
 *
 * The "0/32 panels with high confidence" headline is the first card, emphasised
 * with a ring like the conversions card in IntelligenceCards. It tells the owner
 * how many of the 32 weather-intelligence panels have enough data to render with
 * high confidence — the rest show an honest "not enough data" sentence instead.
 *
 * FYI captions follow the house pattern: text-[11px] text-gray-400 dark:text-gray-500
 * mt-3 text-center italic, placed at the bottom of the panel (same as HeroNumber,
 * ComparisonPair, FrostDates, etc.).
 *
 * Spec references: §7 (data_coverage).
 */

const fmtNum = (v) => Number(v ?? 0).toLocaleString();

// ─── Icons (inline SVG, same style as IntelligenceCards) ───────────────────────
const ICONS = {
  check: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" />
    </svg>
  ),
  calendar: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  clock: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  trophy: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><rect x="6" y="2" width="12" height="12" rx="2" /><path d="M8 22v-4a4 4 0 0 1 8 0v4" /><path d="M12 14v2" />
    </svg>
  ),
  users: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  clipboard: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" />
    </svg>
  ),
  location: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  ),
};

// ─── Tone styles (mirrors IntelligenceCards TONE_STYLES) ───────────────────────
const TONE_STYLES = {
  emerald: { border: "border-t-emerald-400 dark:border-t-emerald-500", iconBg: "bg-emerald-400/10 text-emerald-600 dark:text-emerald-400" },
  sky:     { border: "border-t-sky-400 dark:border-t-sky-500",         iconBg: "bg-sky-400/10 text-sky-600 dark:text-sky-400" },
  indigo:  { border: "border-t-indigo-400 dark:border-t-indigo-500",   iconBg: "bg-indigo-400/10 text-indigo-500 dark:text-indigo-400" },
  teal:    { border: "border-t-teal-400 dark:border-t-teal-500",       iconBg: "bg-teal-400/10 text-teal-600 dark:text-teal-400" },
  blue:    { border: "border-t-blue-400 dark:border-t-blue-500",       iconBg: "bg-blue-400/10 text-blue-600 dark:text-blue-400" },
  amber:   { border: "border-t-amber-400 dark:border-t-amber-500",     iconBg: "bg-amber-400/10 text-amber-600 dark:text-amber-400" },
  orange:  { border: "border-t-orange-400 dark:border-t-orange-500",   iconBg: "bg-orange-400/10 text-orange-600 dark:text-orange-400" },
  gray:    { border: "border-t-gray-300 dark:border-t-gray-700",       iconBg: "bg-gray-200/50 dark:bg-gray-800/60 text-gray-400 dark:text-gray-500" },
};

/**
 * CoverageCard — single stat block.
 * Mirrors IntelligenceCards' InsightCard: coloured top border, icon chip,
 * uppercase title, bold value, optional detail line.
 * Box-shaped: compact width, taller height, centered vertical layout.
 */
const CoverageCard = ({ icon, title, value, detail, tone = "blue", emphasize = false }) => {
  const ts = TONE_STYLES[tone] ?? TONE_STYLES.blue;
  return (
    <div
      className={`relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 border-t-[3px] ${ts.border} rounded-lg p-1.5 flex flex-col items-center justify-center text-center gap-1 ${
        emphasize ? "ring-1 ring-emerald-400/30 dark:ring-emerald-500/20" : ""
      }`}
    >
      <div className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${ts.iconBg}`}>
        {ICONS[icon] ?? ICONS.check}
      </div>
      <div className="min-w-0 w-full">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 truncate mb-0.5">
          {title}
        </div>
        <div className="font-semibold text-base text-gray-900 dark:text-gray-100 leading-tight truncate">
          {value}
        </div>
        {detail && (
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">
            {detail}
          </div>
        )}
      </div>
    </div>
  );
};

const CoveragePanel = ({ panel }) => {
  const f = panel.facts || {};

  const highConf = Number(f.panels_with_high_confidence) || 0;
  const insufficient = Number(f.panels_insufficient) || 0;
  const guestLocPct = Number(f.guest_location_coverage_pct) || 0;
  const sessions90d = Number(f.sessions_90d) || 0;
  const checkinPct = Number(f.sessions_with_checkin_pct) || 0;
  const forecastDays = Number(f.forecast_history_days) || 0;
  const climateYears = Number(f.climate_history_years) || 0;
  const normalsBuilt = f.normals_built;

  // Tone shifts based on data health
  const highConfTone = highConf >= 20 ? "emerald" : highConf >= 10 ? "amber" : "orange";
  const guestLocTone = guestLocPct >= 50 ? "blue" : guestLocPct >= 30 ? "amber" : "orange";
  const normalsTone = normalsBuilt ? "teal" : "gray";

  return (
    <div className="space-y-4">
      {/* ─── Headline: high-confidence panel count ────────────────────────────
          This is a summary of the whole page, not one of the six coverage
          sources below — so it sits above the grid like ForecastTable's hero
          figure, not inside it. Tone shifts with health so the owner can tell
          at a glance whether their data is healthy. */}
      <div className="flex items-baseline gap-3">
        <span className={`text-2xl font-extrabold leading-none ${
          highConfTone === "emerald"
            ? "text-emerald-600 dark:text-emerald-400"
            : highConfTone === "amber"
              ? "text-amber-500 dark:text-amber-400"
              : "text-orange-500 dark:text-orange-400"
        }`}>
          {highConf}/32
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          panels with high confidence
        </span>
      </div>

      {/* ─── Coverage grid — the six data sources ─────────────────────────────
          InsightCard-style blocks (mirrors GA "Period at a Glance"), compact
          single-row grid of box-shaped cards: grid-cols-3 sm:grid-cols-6. */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
        <CoverageCard
          icon="calendar"
          tone="sky"
          title="Forecast"
          value={`${forecastDays}d`}
        />
        <CoverageCard
          icon="clock"
          tone="indigo"
          title="Climate"
          value={`${climateYears}yr`}
        />
        <CoverageCard
          icon="trophy"
          tone={normalsTone}
          title="Normals"
          value={normalsBuilt ? "Yes" : "No"}
        />
        <CoverageCard
          icon="users"
          tone="blue"
          title="Sessions"
          value={fmtNum(sessions90d)}
        />
        <CoverageCard
          icon="clipboard"
          tone="amber"
          title="Check-in"
          value={`${checkinPct}%`}
        />
        <CoverageCard
          icon="location"
          tone={guestLocTone}
          title="Guest loc"
          value={`${guestLocPct}%`}
        />
      </div>

      {/* FYI-style muted captions — house pattern: gray, italic, centered, bottom */}
      {guestLocPct < 30 && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 text-center italic">
          Guest location coverage is low ({guestLocPct}%) — this is the ceiling on every guest-origin panel. Those depend on the CampVantage tracker being installed on the property's own website.
        </p>
      )}

      {insufficient > 0 && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center italic">
          {insufficient} panel{insufficient === 1 ? "" : "s"} have insufficient data and show their honest sentence without evidence.
        </p>
      )}
    </div>
  );
};

export default CoveragePanel;